import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PunnettSquareCalculator = () => {
  const [geneticData, setGeneticData] = useState({
    parent1Genotype: '',
    parent2Genotype: '',
    traitName: '',
    dominantAllele: '',
    recessiveAllele: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGeneticData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setGeneticData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculatePunnettSquare = () => {
    const { parent1Genotype, parent2Genotype, traitName, dominantAllele, recessiveAllele } = geneticData;

    if (!parent1Genotype || !parent2Genotype || !traitName || !dominantAllele || !recessiveAllele) {
      setError('Please fill in all required fields');
      return;
    }

    const validGenotypes = ['AA', 'Aa', 'aa'];
    if (!validGenotypes.includes(parent1Genotype.toUpperCase()) || !validGenotypes.includes(parent2Genotype.toUpperCase())) {
      setError('Please enter valid genotypes (AA, Aa, or aa)');
      return;
    }

    const generatePunnettSquare = () => {
      const parent1 = parent1Genotype.toUpperCase();
      const parent2 = parent2Genotype.toUpperCase();
      const alleles = [dominantAllele.toUpperCase(), recessiveAllele.toUpperCase()];

      const square = [
        [parent2[0] + parent1[0], parent2[1] + parent1[0]],
        [parent2[0] + parent1[1], parent2[1] + parent1[1]]
      ];

      const genotypeCounts = {
        [alleles[0] + alleles[0]]: 0,
        [alleles[0] + alleles[1]]: 0,
        [alleles[1] + alleles[1]]: 0
      };

      square.forEach(row => {
        row.forEach(genotype => {
          const sortedGenotype = genotype.split('').sort().join('');
          genotypeCounts[sortedGenotype]++;
        });
      });

      const phenotypeCounts = {
        dominant: genotypeCounts[alleles[0] + alleles[0]] + genotypeCounts[alleles[0] + alleles[1]],
        recessive: genotypeCounts[alleles[1] + alleles[1]]
      };

      return {
        square,
        genotypeCounts,
        phenotypeCounts,
        genotypeProbabilities: {
          [alleles[0] + alleles[0]]: (genotypeCounts[alleles[0] + alleles[0]] / 4) * 100,
          [alleles[0] + alleles[1]]: (genotypeCounts[alleles[0] + alleles[1]] / 4) * 100,
          [alleles[1] + alleles[1]]: (genotypeCounts[alleles[1] + alleles[1]] / 4) * 100
        },
        phenotypeProbabilities: {
          dominant: (phenotypeCounts.dominant / 4) * 100,
          recessive: (phenotypeCounts.recessive / 4) * 100
        }
      };
    };

    const generateChartData = (results) => {
      const labels = [
        `${dominantAllele}${dominantAllele}`,
        `${dominantAllele}${recessiveAllele}`,
        `${recessiveAllele}${recessiveAllele}`
      ];

      const data = [
        results.genotypeProbabilities[dominantAllele + dominantAllele],
        results.genotypeProbabilities[dominantAllele + recessiveAllele],
        results.genotypeProbabilities[recessiveAllele + recessiveAllele]
      ];

      return {
        labels,
        datasets: [
          {
            label: 'Genotype Probabilities (%)',
            data,
            backgroundColor: [
              'rgba(75, 192, 192, 0.5)',
              'rgba(54, 162, 235, 0.5)',
              'rgba(255, 99, 132, 0.5)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 99, 132, 1)'
            ],
            borderWidth: 1
          }
        ]
      };
    };

    const resultsData = {
      parent1Genotype,
      parent2Genotype,
      traitName,
      dominantAllele,
      recessiveAllele,
      ...generatePunnettSquare()
    };

    setResults(resultsData);
    setChartData(generateChartData(resultsData));
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Punnett Square Calculator | Genetic Cross Analysis Tool</title>
        <meta name="description" content="Calculate genetic cross probabilities with our Punnett Square Calculator. Analyze genotype and phenotype distributions for Mendelian inheritance patterns." />
        <meta name="keywords" content="Punnett square calculator, genetic cross analysis, genotype probability, phenotype probability, genetics tool" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🧬</div>
            <h1 className="text-3xl font-bold text-gray-800">Punnett Square Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Punnett Square Calculator helps you analyze genetic crosses and predict:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Genotype probabilities</li>
              <li>✓ Phenotype probabilities</li>
              <li>✓ Genetic inheritance patterns</li>
              <li>✓ Trait distribution in offspring</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trait Name:</label>
                <input
                  type="text"
                  id="traitName"
                  name="traitName"
                  value={geneticData.traitName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter trait name (e.g., Flower color)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dominant Allele (A):</label>
                <input
                  type="text"
                  id="dominantAllele"
                  name="dominantAllele"
                  value={geneticData.dominantAllele}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter dominant allele symbol (e.g., A)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recessive Allele (a):</label>
                <input
                  type="text"
                  id="recessiveAllele"
                  name="recessiveAllele"
                  value={geneticData.recessiveAllele}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter recessive allele symbol (e.g., a)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent 1 Genotype:</label>
                <select
                  id="parent1Genotype"
                  name="parent1Genotype"
                  value={geneticData.parent1Genotype}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select genotype</option>
                  <option value="AA">AA (Homozygous Dominant)</option>
                  <option value="Aa">Aa (Heterozygous)</option>
                  <option value="aa">aa (Homozygous Recessive)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent 2 Genotype:</label>
                <select
                  id="parent2Genotype"
                  name="parent2Genotype"
                  value={geneticData.parent2Genotype}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select genotype</option>
                  <option value="AA">AA (Homozygous Dominant)</option>
                  <option value="Aa">Aa (Heterozygous)</option>
                  <option value="aa">aa (Homozygous Recessive)</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={geneticData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and probability breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculatePunnettSquare}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Punnett Square
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Genetic Cross Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Parent Genotypes</h3>
                    <p className="text-gray-600">Parent 1: {results.parent1Genotype}</p>
                    <p className="text-gray-600">Parent 2: {results.parent2Genotype}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Trait Information</h3>
                    <p className="text-gray-600">Trait: {results.traitName}</p>
                    <p className="text-gray-600">Dominant Allele: {results.dominantAllele}</p>
                    <p className="text-gray-600">Recessive Allele: {results.recessiveAllele}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Punnett Square</h3>
                  <div className="grid grid-cols-4 gap-2 bg-white rounded-lg p-4">
                    <div className="bg-gray-200 p-2 text-center font-bold">Parent 2</div>
                    <div className="bg-gray-200 p-2 text-center font-bold">{results.parent2Genotype[0]}</div>
                    <div className="bg-gray-200 p-2 text-center font-bold">{results.parent2Genotype[1]}</div>
                    <div className="bg-gray-200 p-2 text-center font-bold">Parent 1</div>
                    <div className="bg-gray-200 p-2 text-center font-bold">{results.parent1Genotype[0]}</div>
                    <div className="p-2 text-center">{results.square[0][0]}</div>
                    <div className="p-2 text-center">{results.square[0][1]}</div>
                    <div className="bg-gray-200 p-2 text-center font-bold">{results.parent1Genotype[1]}</div>
                    <div className="p-2 text-center">{results.square[1][0]}</div>
                    <div className="p-2 text-center">{results.square[1][1]}</div>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Probability (%)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Genotypes'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Genotype Probabilities',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Genotype Distribution</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">
                        {results.dominantAllele + results.dominantAllele}:
                      </span>
                      {results.genotypeProbabilities[results.dominantAllele + results.dominantAllele]}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">
                        {results.dominantAllele + results.recessiveAllele}:
                      </span>
                      {results.genotypeProbabilities[results.dominantAllele + results.recessiveAllele]}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">
                        {results.recessiveAllele + results.recessiveAllele}:
                      </span>
                      {results.genotypeProbabilities[results.recessiveAllele + results.recessiveAllele]}%
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Phenotype Distribution</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">
                        Dominant Phenotype:
                      </span>
                      {results.phenotypeProbabilities.dominant}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">
                        Recessive Phenotype:
                      </span>
                      {results.phenotypeProbabilities.recessive}%
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Punnett Squares <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>Punnett squares are a fundamental tool in genetics used to predict the probability of offspring inheriting specific traits. They help in:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Genetic Counseling: Predicting trait inheritance in families</li>
                <li>Animal Breeding: Understanding trait distribution in offspring</li>
                <li>Medical Genetics: Assessing genetic disorder risks</li>
                <li>Plant Breeding: Developing new crop varieties</li>
              </ul>
              <p className="italic mt-2">Note: Punnett squares assume independent assortment and no linkage between genes.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Punnett Square Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Punnett Square Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Define Your Trait</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the trait name you want to analyze</li>
                  <li>Specify the dominant and recessive alleles</li>
                  <li>Choose the genotypes of both parents</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Generate Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Punnett Square" to generate the results</li>
                  <li>View the Punnett square grid showing all possible combinations</li>
                  <li>Check genotype and phenotype probabilities</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Analyze the distribution of genotypes and phenotypes</li>
                  <li>Use the chart to visualize probability distributions</li>
                  <li>Consider the implications for trait inheritance</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Punnett squares work best for single gene traits</li>
                  <li>Complex traits may require more advanced analysis</li>
                  <li>Consider environmental factors affecting trait expression</li>
                  <li>Consult genetics experts for complex cases</li>
                  <li>Use results as a probability guide, not absolute prediction</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PunnettSquareCalculator;